import { cloneDeep } from 'lodash';
import React from 'react';
import { IoCloseCircle } from 'react-icons/io5';
import { ITranslations } from '../data/strings';

import { EAge, IFamilyMember } from '../models/IFamily';
import { ELCID } from '../models/IMediaToolkit';
import { Avatar } from './Avatar';
import { SelectableButton } from './SelectableButton';

export interface IFamilyMemberPickerProps {
    familyMember: IFamilyMember;
    index: number;
    readonly: boolean;
    lcid: ELCID;
    translations: ITranslations;
    updateMember: (data: IFamilyMember, idx: number) => void;
    deleteMember: (idx: number) => void;
    removable?: boolean;
}

const validAges = [EAge['0-24 Months'], EAge['2-5 Years'], EAge['6-12 Years'], EAge['13-18 Years'], EAge['Adult']];

export const FamilyMemberPicker: React.FunctionComponent<IFamilyMemberPickerProps> = (props) => {
    return (
        <div className="flex gap-2">
            <div className="border p-3 border-aap-orange-500 rounded-md bg-white lg:flex gap-4 mb-3 flex-1" key={`picker-familymember-${props.index}`}>
                <div className="flex flex-1 gap-4">
                    <Avatar
                        avatarId={props.familyMember.avatar}
                        uniqueKey={`avatar-familymember-${props.index}-name`}
                        editable={!props.readonly}
                        lcid={props.lcid}
                        translations={props.translations}
                        updateAvatar={(idx) => {
                            const tempMember = cloneDeep(props.familyMember);
                            tempMember.avatar = idx;
                            props.updateMember(tempMember, props.index);
                        }}
                    />
                    <div className="flex-1 gap-4">
                        <div className="flex-1 mb-3 lg:mb-0 md:basis-64 xl:basis-1/2 md:flex-grow-0 md:flex-shrink-0">
                            <small className="block">{props.translations.familyMembersName} *</small>
                            <input
                                value={props.familyMember.name}
                                onChange={(ev) => {
                                    const tempMember = cloneDeep(props.familyMember);
                                    tempMember.name = ev.target.value;
                                    props.updateMember(tempMember, props.index);
                                }}
                                type="text"
                                id={`input-familymember-${props.index}-name`}
                                key={`input-familymember-${props.index}-name`}
                                className={`form-control rounded-sm border-2 border-aap-gray-200 text-xl p-2 w-full transition-all h-12 ${!props.familyMember.name ? `border-aap-red-500` : `border-aap-green-500`}`}
                                disabled={props.readonly}
                            />
                        </div>
                    </div>
                </div>
                <div className="flex-1">
                    <small className="block">{props.translations.age}</small>
                    <div className="flex gap-2">
                        {validAges.map((age, idx) => (
                            <SelectableButton
                                checked={props.familyMember.age === age}
                                key={`age-familymember-${props.index}-button-${idx}`}
                                id={`btn_familyMemberAge_${age}_${ELCID[props.lcid]}`}
                                onClick={() => {
                                    const tempMember = cloneDeep(props.familyMember);
                                    tempMember.age = age;

                                    props.updateMember(tempMember, props.index);
                                }}
                                variant="age"
                                additionalClasses={`h-12 flex-1`}
                                readonly={props.readonly}
                            >
                                <span className="text-xs md:text-sm">{props.translations[age]}</span>
                            </SelectableButton>
                        ))}
                    </div>
                </div>
            </div>
            {props.removable && !props.readonly ? (
                <button type="button" onClick={() => props.deleteMember(props.index)} className="text-aap-red-500" id={`btn_deleteFamilyMember_${ELCID[props.lcid]}`}>
                    <IoCloseCircle size={24} />
                </button>
            ) : null}
        </div>
    );
};
